import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { headerItems } from '@/config/contents';
import { getAvailableTimezones, getUserTimezone } from '@/lib/timezones';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Channel } from '@prisma/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toZonedTime } from 'date-fns-tz';
import { useAtom } from 'jotai';
import { Check, Clock } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
} from '@/components/ui/command';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

import { activeWorkspaceAtom, headerAtom, playbookUrlAtom } from '../atoms';
import { useCreateChannelModal } from '../store/use-create-workspace-modal';

const formSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    collaborationType: z.enum(['webinar', 'content', 'in-person']),
    eventStartDate: z.date().optional(),
    eventEndDate: z.date().optional(),
    timezone: z.string().default(getUserTimezone()),
});

type FormValues = z.infer<typeof formSchema>;

export function CreateChannelModal() {
    const queryClient = useQueryClient();
    const { openChannelCreateModal, setOpenChannelCreateModal } =
        useCreateChannelModal();
    const [activeWorkspace] = useAtom(activeWorkspaceAtom);
    const router = useRouter();
    const [, setHeader] = useAtom(headerAtom);
    // const [, setActivePath] = useAtom(activePathAtom);
    const [userTimezone, setUserTimezone] = useState<string | null>(null);
    const [, setPlaybookUrl] = useAtom(playbookUrlAtom);

    useEffect(() => {
        setUserTimezone(getUserTimezone());
    }, []);

    // Set default start and end times
    const defaultStartDateTime = new Date();
    defaultStartDateTime.setHours(18, 0, 0, 0);

    const defaultEndDateTime = new Date();
    defaultEndDateTime.setHours(19, 0, 0, 0);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: '',
            collaborationType: 'in-person',
            eventStartDate: defaultStartDateTime,
            eventEndDate: defaultEndDateTime,
            timezone: userTimezone || getUserTimezone(),
        },
    });

    useEffect(() => {
        if (userTimezone) {
            form.setValue('timezone', userTimezone);
        }
    }, [userTimezone, form]);

    const mutation = useMutation<Channel, Error, FormValues>({
        mutationFn: async (values) => {
            setPlaybookUrl('/playbooks/playbook-blank.md');
            if (!activeWorkspace?.id) {
                throw new Error('No active workspace ID');
            }

            const domain = activeWorkspace.domain
                ? activeWorkspace.domain.trim()
                : activeWorkspace.id;

            if (
                (values.collaborationType === 'in-person' ||
                    values.collaborationType === 'webinar') &&
                values.eventStartDate &&
                values.eventEndDate
            ) {
                // Convert start date to specified timezone
                const eventStartDateZoned = toZonedTime(
                    values.eventStartDate,
                    values.timezone
                );

                const eventEndDateZoned = toZonedTime(
                    values.eventEndDate,
                    values.timezone
                );

                // Convert end date to specified timezone
                const endDateZoned = toZonedTime(
                    values.eventEndDate,
                    values.timezone
                );

                // Infer collaboration dates:
                // Start date is now (creation time)
                // End date is the event end date, or if no event, 2 weeks from now
                const startDate = toZonedTime(new Date(), values.timezone);

                const response = await fetch('/api/channels', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        name: values.name,
                        workspaceId: activeWorkspace.id,
                        domain,
                        collaborationType: values.collaborationType,
                        startDate: startDate.toISOString(),
                        endDate: endDateZoned.toISOString(),
                        eventStartDate: eventStartDateZoned.toISOString(),
                        eventEndDate: eventEndDateZoned.toISOString(),
                        timezone: values.timezone,
                    }),
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result.error);
                return result.data;
            }
        },
        onSuccess: (channel) => {
            form.reset();
            setHeader({
                one: activeWorkspace?.name || '',
                two: channel.name,
                postFix: headerItems.channelHeader,
            });

            queryClient.invalidateQueries({
                queryKey: ['channels', activeWorkspace?.id],
            });
            setOpenChannelCreateModal(false);
            router.push(
                `/dashboard/workspace/${activeWorkspace?.id}/campaign/${channel.id}`
            );
            toast.success('Partner collaboration created successfully');
        },
        onError: (error) => {
            toast.error(
                error.message || 'Failed to create partner collaboration'
            );
        },
    });

    const onSubmit = (values: FormValues) => {
        mutation.mutate(values);
    };

    return (
        <Dialog
            open={openChannelCreateModal}
            onOpenChange={setOpenChannelCreateModal}
        >
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Create New Partner Collaboration</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-6"
                    >
                        <div className="grid gap-6">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Name</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter the name of the collaboration"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="collaborationType"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Collaboration Type
                                        </FormLabel>
                                        <FormControl>
                                            <ToggleGroup
                                                type="single"
                                                value={field.value}
                                                onValueChange={(value) => {
                                                    field.onChange(
                                                        value || field.value
                                                    );
                                                }}
                                                className="flex justify-start gap-2"
                                            >
                                                <ToggleGroupItem
                                                    value="in-person"
                                                    className="flex-1"
                                                >
                                                    In-Person Event
                                                </ToggleGroupItem>
                                                <ToggleGroupItem
                                                    value="webinar"
                                                    className="flex-1"
                                                    disabled
                                                >
                                                    Webinar
                                                </ToggleGroupItem>
                                                <ToggleGroupItem
                                                    value="content"
                                                    className="flex-1"
                                                    disabled
                                                >
                                                    Content Collaboration
                                                </ToggleGroupItem>
                                            </ToggleGroup>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {form.watch('collaborationType') ===
                                'in-person' && (
                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Event Start</Label>
                                            <FormField
                                                control={form.control}
                                                name="eventStartDate"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <DateTimePicker
                                                                date={
                                                                    field.value
                                                                }
                                                                setDate={
                                                                    field.onChange
                                                                }
                                                                label="Select event start date and time"
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Event End</Label>
                                            <FormField
                                                control={form.control}
                                                name="eventEndDate"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <DateTimePicker
                                                                date={
                                                                    field.value
                                                                }
                                                                setDate={
                                                                    field.onChange
                                                                }
                                                                label="Select event end date and time"
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-0">
                                        <FormField
                                            control={form.control}
                                            name="timezone"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>
                                                        Timezone
                                                    </FormLabel>
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <FormControl>
                                                                <Button
                                                                    variant="outline"
                                                                    role="combobox"
                                                                    className={cn(
                                                                        'w-full justify-between',
                                                                        !field.value &&
                                                                            'text-muted-foreground'
                                                                    )}
                                                                >
                                                                    {field.value
                                                                        ? field.value.replace(
                                                                              /_/g,
                                                                              ' '
                                                                          )
                                                                        : 'Select timezone'}
                                                                    <Clock className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                                </Button>
                                                            </FormControl>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-[300px] p-0">
                                                            <Command>
                                                                <CommandInput placeholder="Search timezone..." />
                                                                <CommandEmpty>
                                                                    No timezone
                                                                    found.
                                                                </CommandEmpty>
                                                                <CommandGroup className="max-h-[300px] overflow-auto">
                                                                    {getAvailableTimezones().map(
                                                                        (
                                                                            timezone
                                                                        ) => (
                                                                            <CommandItem
                                                                                key={
                                                                                    timezone
                                                                                }
                                                                                value={
                                                                                    timezone
                                                                                }
                                                                                onSelect={() => {
                                                                                    field.onChange(
                                                                                        timezone
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <Check
                                                                                    className={cn(
                                                                                        'mr-2 h-4 w-4',
                                                                                        timezone ===
                                                                                            field.value
                                                                                            ? 'opacity-100'
                                                                                            : 'opacity-0'
                                                                                    )}
                                                                                />
                                                                                {timezone.replace(
                                                                                    /_/g,
                                                                                    ' '
                                                                                )}
                                                                            </CommandItem>
                                                                        )
                                                                    )}
                                                                </CommandGroup>
                                                            </Command>
                                                        </PopoverContent>
                                                    </Popover>
                                                    <FormDescription>
                                                        All times will be
                                                        displayed in this
                                                        timezone
                                                    </FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <DialogFooter className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setOpenChannelCreateModal(false)}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={mutation.isPending}>
                                Create Collaboration
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
